import React, {memo, useCallback, useEffect, useRef} from 'react'
import {
  Paper,
  Typography,
  IconButton,
  Theme,
  Tooltip,
  Box,
} from '@material-ui/core'
import {makeStyles} from '@material-ui/core/styles'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import PauseIcon from '@material-ui/icons/Pause'

import {Message} from '../types/chat'
import {AudioPlayerService} from '../lib/AudioPlayerService'
import {LAST_MESSAGE_LINK_LANGUAGES} from '../utils/lastMessageLinkText'

// Define keyframes for the pulsing animation
const pulseKeyframes = {
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1.5)', // Start at base scale
    },
    '50%': {
      transform: 'scale(1.8)', // Scale up
    },
    '100%': {
      transform: 'scale(1.5)', // Scale back down
    },
  },
}

const useStyles = makeStyles((theme: Theme) => ({
  // Include keyframes in the styles
  ...pulseKeyframes,
  userMessage: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    maxWidth: '70%',
    wordWrap: 'break-word' as const,
    backgroundColor: '#65A765',
    color: theme.palette.primary.contrastText,
    marginLeft: 'auto',
    marginRight: theme.spacing(0),
    borderRadius: theme.spacing(1),
  },
  aiMessage: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    maxWidth: '70%',
    wordWrap: 'break-word' as const,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(0),
    marginRight: 'auto',
    borderRadius: theme.spacing(1),
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  undoMessage: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    maxWidth: '70%',
    wordWrap: 'break-word' as const,
    backgroundColor: '#FFFFC5',
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(0),
    marginRight: 'auto',
    borderRadius: theme.spacing(1),
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  messageContent: {
    flex: 1,
  },
  messageLink: {
    color: theme.palette.primary.main,
    wordBreak: 'break-all',
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  audioControls: {
    marginLeft: theme.spacing(1),
  },
  // Style for the playing icon
  playingIcon: {
    color: theme.palette.error.main, // Red color
    animation: '$pulse 1.5s ease-in-out infinite', // Apply pulsing animation
    transform: 'scale(2)', // Set base scale
  },
  // Style for the non-playing icon (optional, if you want consistent size)
  pausedIcon: {
    transform: 'scale(1.2)', // Keep consistent base scale even when paused
  },
  messagesContainer: {
    width: '100%',
  },
  bottomPadding: {
    paddingBottom: theme.spacing(1),
    height: '1px',
  },
  optionsContainer: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'end',
  },
  optionButton: {
    textTransform: 'none',
  },
  messageRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  undoButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  forceFontSize: {
    fontSize: '1rem', // Force 16px with !important
    '& *': {
      fontSize: '1rem', // Force all nested elements to 16px
    },
  },
}))

/**
 * Converts URLs and markdown links in text to clickable HTML links
 * @param text The text to process
 * @param linkClassName CSS class to apply to links
 * @returns JSX with clickable links
 */

const convertUrlsToLinks = (
  text: string,
  linkClassName: string,
  selectedLanguageCode: string | null,
  textBodyClassName: string
): React.ReactNode => {
  text = text.replace(/^"(.*)"$/, '$1')

  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
  const htmlTagRegex = /(<br\s*\/?>)/gi
  const result: React.ReactNode[] = []
  const segments = text.split(htmlTagRegex)

  let linkCounter = 0
  const lastMessageTextLink =
    LAST_MESSAGE_LINK_LANGUAGES.find(
      (lang) => lang.code === selectedLanguageCode
    )?.text ||
    LAST_MESSAGE_LINK_LANGUAGES.find((lang) => lang.code === 'en-US')?.text ||
    'Click here'

  const convertMarkdownLinks = (text: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = []
    let lastIndex = 0
    let match

    markdownLinkRegex.lastIndex = 0
    while ((match = markdownLinkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index))
      }

      nodes.push(
        <a
          key={`link-${linkCounter++}`}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
        >
          {lastMessageTextLink}
        </a>
      )
      lastIndex = markdownLinkRegex.lastIndex
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex))
    }

    return nodes
  }

  for (let i = 0; i < segments.length; i++) {
    const raw = segments[i]
    if (!raw) continue

    if (raw.match(/^<br\s*\/?>$/i)) {
      result.push(<br key={`br-${i}`} />)
      continue
    }

    const pattern = /(\|\|[^|]+\|\||\{[^}]+\})/g
    let lastIndex = 0
    let match

    while ((match = pattern.exec(raw)) !== null) {
      const matchStart = match.index
      const matchEnd = pattern.lastIndex

      // Text before the current block
      if (matchStart > lastIndex) {
        const outsideText = raw.slice(lastIndex, matchStart)
        result.push(...convertMarkdownLinks(outsideText))
      }

      const block = match[0]
      const isBold = block.startsWith('{')
      const content = block.slice(isBold ? 1 : 2, isBold ? -1 : -2)
      const parsedContent = convertMarkdownLinks(content)

      result.push(
        <div
          key={`${isBold ? 'q' : 's'}-${i}-${matchStart}`}
          className={textBodyClassName}
          style={{
            fontSize: '16px',
            WebkitTextSizeAdjust: '100%',
            textSizeAdjust: '100%',
          }}
        >
          {isBold ? <strong>{parsedContent}</strong> : parsedContent}
        </div>
      )

      lastIndex = matchEnd
    }

    // Remaining text after the last match
    if (lastIndex < raw.length) {
      const remaining = raw.slice(lastIndex)
      result.push(...convertMarkdownLinks(remaining))
    }
  }

  return result
}

/**
 * Props for the MessageList component
 */
interface MessageListProps {
  /** Array of messages to display */
  messages: Message[]
  /** Function to call when audio playback is requested or stopped */
  onPlayAudio: (messageId: string, audioBlob: Blob | null) => void // audioBlob can be null for streamed AI audio
  /** ID of the message currently playing audio, if any */
  playingMessageId: string | null
  /** Currently playing audio data, if any */
  // currentlyPlayingAudio?: string | null
  /** Whether audio is currently playing */
  // isAudioPlaying?: boolean
  currentAiAudioPlayer: AudioPlayerService | null // Added prop
  latestAiMessageId: string | null // Added prop for the ID of the latest AI message being streamed
  onUndoClick: (option: string) => void
  undoMessageIndex: number | null
  selectedLanguageCode: string | null // Optional prop for selected language
}

/**
 * Props for the MessageItem component
 */
interface MessageItemProps {
  /** The message to display */
  message: Message
  /** Function to call when audio playback is requested or stopped */
  onPlayAudio: (messageId: string, audioBlob: Blob | null) => void // audioBlob can be null
  /** ID of the message currently playing audio, if any */
  playingMessageId: string | null
  /** Currently playing audio data, if any */
  // currentlyPlayingAudio?: string | null
  /** Whether audio is currently playing */
  // isAudioPlaying?: boolean
  currentAiAudioPlayer: AudioPlayerService | null // Added prop
  latestAiMessageId: string | null // Added prop
  onUndoClick?: (option: string) => void
  undoMessageIndex?: number | null
  messageIndex: number | null
  selectedLanguageCode: string | null
}

/**
 * Individual message component that displays a single chat message
 */
const MessageItem = memo(
  ({
    message,
    onPlayAudio,
    playingMessageId,
    currentAiAudioPlayer,
    latestAiMessageId, // Destructure new prop
    onUndoClick,
    undoMessageIndex,
    messageIndex,
    selectedLanguageCode,
  }: MessageItemProps) => {
    const classes = useStyles()
    const isThisMessagePlaying =
      playingMessageId === message.id || // For blob-based playback
      (currentAiAudioPlayer?.getIsPlaying() && // For live stream
        !message.isUser &&
        message.id === latestAiMessageId)

    const handlePlayAudio = useCallback(() => {
      // For AI messages, onPlayAudio will now primarily signal an intent to play/pause the stream
      // The actual stream control happens in Chat.tsx via currentAiAudioPlayerRef
      // For user messages or old AI messages with `audio` Blob, it uses the blob.
      onPlayAudio(message.id, message.audio || null)
    }, [message.id, message.audio, onPlayAudio])

    return (
      <Paper
        className={
          messageIndex === undoMessageIndex
            ? classes.undoMessage
            : message.isUser
            ? classes.userMessage
            : classes.aiMessage
        }
        role="listitem"
        aria-label={`${message.isUser ? 'You' : 'AI'}: ${message.content}`}
      >
        <div className={classes.messageRow}>
          <Typography
            className={classes.messageContent}
            component="div"
            variant="body2"
          >
            {convertUrlsToLinks(
              message.content,
              classes.messageLink,
              selectedLanguageCode,
              classes.forceFontSize
            )}
          </Typography>
          {((!message.isUser && message.audio) ||
            (!message.isUser && message.id === latestAiMessageId)) && (
            <Tooltip
              title={
                isThisMessagePlaying
                  ? 'Pause message audio'
                  : 'Play message audio'
              }
            >
              <IconButton
                className={classes.audioControls}
                size="small"
                onClick={handlePlayAudio}
                aria-label={
                  isThisMessagePlaying
                    ? 'Pause message audio'
                    : 'Play message audio'
                }
              >
                {isThisMessagePlaying ? (
                  <PauseIcon className={classes.playingIcon} />
                ) : (
                  <VolumeUpIcon className={classes.pausedIcon} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </div>
        <div>
          {!message.isUser && onUndoClick && message.id !== latestAiMessageId && (
            <div className={classes.optionsContainer}>
              <IconButton
                onClick={() => onUndoClick(message.id!)}
                aria-label="Undo last action"
                style={{padding: 0}}
              >
                <img src="/undo.png" alt="" width={24} height={24} />
              </IconButton>
            </div>
          )}
        </div>
      </Paper>
    )
  }
)

MessageItem.displayName = 'MessageItem'

/**
 * Component that displays a list of chat messages
 */
const MessageList: React.FC<MessageListProps> = memo(
  ({
    messages,
    onPlayAudio,
    playingMessageId,
    currentAiAudioPlayer,
    latestAiMessageId,
    onUndoClick,
    undoMessageIndex,
    selectedLanguageCode,
  }) => {
    const classes = useStyles()
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const eofMessagePresent = messages.some(
      (msg) => msg.isUser === false && msg.field === '--end of form field--'
    )

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({behavior: 'smooth'})
    }

    useEffect(() => {
      scrollToBottom()
    }, [messages]) // Scroll whenever messages change

    return (
      <Box
        className={classes.messagesContainer}
        role="list"
        aria-label="Message history"
      >
        {messages.map((message, index) => (
          <MessageItem
            key={message.id || index}
            message={message}
            onPlayAudio={onPlayAudio}
            playingMessageId={playingMessageId}
            currentAiAudioPlayer={currentAiAudioPlayer}
            latestAiMessageId={latestAiMessageId} // Pass down the prop
            onUndoClick={eofMessagePresent ? undefined : onUndoClick}
            undoMessageIndex={undoMessageIndex}
            messageIndex={index}
            selectedLanguageCode={selectedLanguageCode || null} // Pass selected language code
          />
        ))}
        {/* Dummy div to scroll to */}
        <div ref={messagesEndRef} />
        {/* Preserve bottom padding if needed, or remove if the ref handles the space */}
        {messages.length > 0 && <div className={classes.bottomPadding}></div>}
      </Box>
    )
  }
)

MessageList.displayName = 'MessageList'

export default MessageList
